import { useCustomForm } from "common/hooks/src/api/useCustomForm";
import { Button } from "components/button";
import { CardWithTitle } from "components/card/CardWithTitle";
import { InputField, ResizableInputField } from "components/fields/InputField";
import { useCreateBonDeCommande } from "modules/bonDeCommande/api";
import { FormEvent, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useGetArticles } from "modules/article/api";
import {
  SelectDevis,
  SelectIntervenants,
  SelectClient,
  SelectStatus,
  SelectArticle,
  SelectReductionType,
} from "modules/bonDeCommande/components";
import { useSearchParams } from "react-router-dom";
import {
  CreateBonDeCommandePayload,
  BonDeCommandeLineItem,
  BON_DE_COMMANDE_STATUS,
} from "modules/bonDeCommande/types/bonDeCommande.interface";
import { ReductionType } from "modules/devis/types/devis.interface";

const CreateBonDeCommande = () => {
  const [searchParams] = useSearchParams();
  const commandeId = searchParams.get("commandeId");
  const devisId = searchParams.get("devisId");

  const { mutate } = useCreateBonDeCommande(commandeId);

  const { data: articlesData } = useGetArticles(undefined);
  const { formValues, setFormValues } =
    useCustomForm<CreateBonDeCommandePayload>({
      clientId: "",
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      interveners: [],
      lineItems: [
        {
          articleId: "",
          priceHT: 0,
          quantity: 1,
          vatAmount: 20,
          reductionAmount: 0,
          reductionType: ReductionType.PERCENTAGE,
          totalTTC: 0,
          totalHT: 0,
          description: "",
        },
      ],
      totalTTC: 0,
      totalHT: 0,
      status: BON_DE_COMMANDE_STATUS.DRAFT,
    });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate(formValues);
  };

  const handleAddLineItem = () => {
    setFormValues({
      ...formValues,
      lineItems: [
        ...formValues.lineItems,
        {
          articleId: "",
          quantity: 1,
          reductionType: ReductionType.PERCENTAGE,
          reductionAmount: 0,
          vatAmount: 0,
          priceHT: 0,
          description: "",
          totalTTC: 0,
          totalHT: 0,
        },
      ],
    });
  };

  const handleRemoveLineItem = (index: number) => {
    const updatedLineItems = formValues.lineItems.filter(
      (_, idx) => idx !== index
    );
    const newTotalBonDeCommande = updatedLineItems.reduce(
      (acc, item) => acc + item.totalTTC,
      0
    );
    setFormValues({
      ...formValues,
      lineItems: updatedLineItems,
      totalTTC: Number(newTotalBonDeCommande.toFixed(2)),
      totalHT: Number(newTotalBonDeCommande.toFixed(2)),
    });
  };

  const handleLineItemChange = (
    index: number,
    field: keyof BonDeCommandeLineItem,
    value: any
  ) => {
    const updatedLineItems = formValues.lineItems.map((item, idx) => {
      if (idx === index) {
        const newItem = { ...item, [field]: value };

        if (field === "articleId") {
          const allArticles =
            articlesData?.pages.flatMap((page) => page.data) || [];
          const selectedArticle = allArticles.find((a) => a._id === value);
          if (selectedArticle) {
            newItem.priceHT = selectedArticle.priceHT;
            newItem.description = selectedArticle.label;
          }
        }

        const priceHT = Number(newItem.priceHT) || 0;
        const vatPercent = Number(newItem.vatAmount) || 0; // TVA %
        const reductionAmount = Number(newItem.reductionAmount) || 0;
        const reductionType = newItem.reductionType;

        // Calcul des nouvelles valeurs
        const ht = priceHT;
        const vatAmount = ht * (vatPercent / 100);
        const baseAmount = ht + vatAmount;

        let reduction =
          reductionType === ReductionType.PERCENTAGE
            ? baseAmount * (reductionAmount / 100)
            : reductionAmount;

        const total = Math.max(0, baseAmount - reduction);

        // Mise à jour des valeurs calculées
        newItem.vatAmount = vatPercent; // Conserver le pourcentage
        newItem.totalTTC = Number(total.toFixed(2));
        newItem.totalHT = Number(ht.toFixed(2));

        return newItem;
      }
      return item;
    });

    // Calcul du nouveau total global
    const newTotalBonDeCommande = updatedLineItems.reduce(
      (acc, item) => acc + item.totalTTC,
      0
    );

    setFormValues((prev) => ({
      ...prev,
      lineItems: updatedLineItems,
      totalTTC: Number(newTotalBonDeCommande.toFixed(2)),
      totalHT: Number(newTotalBonDeCommande.toFixed(2)),
    }));
  };

  const { totalHT, totalRemise, totalVAT } = useMemo(() => {
    return formValues.lineItems.reduce(
      (acc, item) => {
        const priceHT =
          item.priceHT !== undefined
            ? item.priceHT
            : item.totalHT / (1 + item.vatAmount / 100);
        const ht = priceHT;
        const vat = ht * (item.vatAmount / 100);
        const baseAmount = ht + vat;
        const reduction =
          item.reductionType === ReductionType.PERCENTAGE
            ? baseAmount * ((item.reductionAmount ?? 0) / 100)
            : item.reductionAmount ?? 0;

        acc.totalHT += ht;
        acc.totalRemise += reduction;
        acc.totalVAT += vat;
        return acc;
      },
      { totalHT: 0, totalRemise: 0, totalVAT: 0 }
    );
  }, [formValues.lineItems]);

  const totalHTFinal = totalHT - totalRemise;
  const totalTTC = totalHTFinal + totalVAT;

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <CardWithTitle title="Nouveau Bon de commande">
          <div className="mb-5">
            <SelectDevis
              formValues={formValues}
              setFormValues={setFormValues}
              devisId={devisId}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <SelectClient
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <InputField
              variant="auth"
              label="Échéance"
              id="dueDate"
              type="date"
              extra="mb-3"
              value={
                formValues.dueDate
                  ? formValues.dueDate.toISOString().split("T")[0]
                  : ""
              }
              placeholder="Date d'échéance"
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  dueDate: e.target.value ? new Date(e.target.value) : null,
                })
              }
            />
            <SelectIntervenants
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <SelectStatus
              formValues={formValues}
              setFormValues={setFormValues}
            />
          </div>

          <div className="mt-6">
            <h3 className="mb-4 text-xl font-semibold">Articles</h3>
            {formValues.lineItems.map((item, index) => (
              <div
                key={index}
                className="relative mb-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4"
              >
                <button
                  type="button"
                  onClick={() => handleRemoveLineItem(index)}
                  className="absolute right-3 top-3 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7">
                  <div className="col-span-1">
                    <SelectArticle
                      value={item.articleId}
                      onChange={(articleId) =>
                        handleLineItemChange(index, "articleId", articleId)
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Prix HT"
                      id={`priceHT-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.priceHT}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "priceHT",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Quantité"
                      id={`quantity-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      min={1}
                      value={item.quantity}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "quantity",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="TVA %"
                      id={`vatAmount-${index}`}
                      placeholder="0.00"
                      type="number"
                      extra="mb-3"
                      value={item.vatAmount || ""}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "vatAmount",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Réduction"
                      id={`reductionAmount-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.reductionAmount ?? ""}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "reductionAmount",
                          parseFloat(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <SelectReductionType
                      value={item.reductionType}
                      onChange={(reductionType) =>
                        handleLineItemChange(
                          index,
                          "reductionType",
                          reductionType
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Total TTC"
                      id={`total-${index}`}
                      placeholder="0.00"
                      type="number"
                      extra="mb-3"
                      value={item.totalTTC}
                      disabled
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <ResizableInputField
                    variant="auth"
                    label="Description"
                    id={`description-${index}`}
                    extra="mb-3"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) =>
                      handleLineItemChange(index, "description", e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddLineItem}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-2.5 text-xs font-medium text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Ajouter un article
            </button>
          </div>

          <div className="mt-6 flex justify-end text-left">
            <CardWithTitle title="Totaux">
              <div className="grid w-full grid-cols-2 gap-3 sm:w-80">
                <div className="text-sm font-medium text-gray-600">
                  Total HT
                </div>
                <div className="text-right text-sm">
                  {totalHT.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>
                <div className="text-sm font-medium text-gray-600">
                  Remise générale
                </div>
                <div className="text-right text-sm">
                  {totalRemise.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                  €
                </div>
                <div className="text-sm font-medium text-gray-600">
                  Total HT Final
                </div>
                <div className="text-right text-sm">
                  {totalHTFinal
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                  €
                </div>
                <div className="text-sm font-medium text-gray-600">TVA</div>
                <div className="text-right text-sm">
                  {totalVAT.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>
                <div className="text-sm font-medium text-gray-600">
                  Total TTC
                </div>
                <div className="text-right text-sm font-semibold">
                  {totalTTC.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>
              </div>
            </CardWithTitle>
          </div>
        </CardWithTitle>

        <div className="mt-2 flex w-full justify-end">
          <div className="w-full max-w-xs">
            <Button onClick={handleSubmit} variant="primary">
              Créer le bon de commande
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default CreateBonDeCommande;
